import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as SanitizeSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import type { PluggableList } from 'unified';
import { escapeCurrencyDollars } from 'src/lib/markdownMath';
import { GuardedImage } from './GuardedImage';

// Citation previews wrap quoted lines in this class to highlight them.
const CITATION_HIGHLIGHT_CLASS = 'bg-yellow-100';

const schema: SanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span ?? []), ['className', CITATION_HIGHLIGHT_CLASS]]
  }
};

// rehype-katex runs after the sanitiser so its generated markup survives.
const rehypePlugins: PluggableList = [
  rehypeRaw,
  [rehypeSanitize, schema],
  [rehypeKatex, { throwOnError: false, strict: false }]
];
const remarkPlugins: PluggableList = [remarkMath, remarkGfm];
const components: Components = { img: GuardedImage };

/**
 * Render markdown that may embed raw HTML from stored or LLM-authored content.
 *
 * Raw HTML is reduced to GitHub's sanitise allowlist plus the citation
 * highlight, and images load only from trusted origins.
 */
export default function SanitizedMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown rehypePlugins={rehypePlugins} remarkPlugins={remarkPlugins} components={components}>
      {escapeCurrencyDollars(children)}
    </ReactMarkdown>
  );
}
